// Shared pagination types and request-parsing helpers used by both
// `server/` (route handlers + storage) and `client/` (TanStack Query
// hooks). Keeping the contract in one file prevents the wire shape
// from drifting between the two ends.
//
// The augmented insureds-list row type lives here too because the
// /api/insureds response is a strict superset of the `Insured` schema
// type — see CSR-674 spec, Locked Decision #6.

import { IN_FORCE_STATUSES } from "./schema";
import type { Insured } from "./schema";

// ─── Sort whitelists ───
export const POLICY_SORT_FIELDS = [
  "policyNumber",
  "customerName",
  "carrier",
  "status",
  "premium",
  "effectiveDate",
  "expirationDate",
  "createdAt",
] as const;
export type PolicySortField = (typeof POLICY_SORT_FIELDS)[number];

export const INSURED_SORT_FIELDS = [
  "customerName",
  "dotNumber",
  "activePolicyCount",
  "cycle",
  "status",
  // CSR-674 R-002: sort by current in-force premium per insured. Powers
  // the dashboard "Premium Contributors" dialog. Computed in SQL via
  // SUM(p.premium) FILTER (in-force-today predicate); see storage.ts
  // `getInsuredsPage` and the `activePremium` field on
  // `AugmentedInsuredRow`.
  "premium",
] as const;
export type InsuredSortField = (typeof INSURED_SORT_FIELDS)[number];

export type SortDir = "asc" | "desc";

// ─── Status filters ───
// Literal status values currently in the policies table.
export const POLICY_STATUS_LITERALS = [
  "active",
  "pending cancel",
  "renewing",
  "reinstated",
  "non-renewal",
  // CSR-1042 — mirrors the `NORMALIZED_STATUSES` insertion point in
  // `server/sync.ts` so the filter whitelist accepts `?status=renewal+offer`.
  "renewal offer",
  "expired",
  "renewed",
  "replaced",
  "cancelled",
  // CSR-1725/CSR-1699 — mirrors the `NORMALIZED_STATUSES` insertion point in
  // `server/sync.ts` so the filter whitelist accepts `?status=flat+cancel`.
  "flat cancel",
] as const;
export type PolicyStatusLiteral = (typeof POLICY_STATUS_LITERALS)[number];

// Aliases that map to date-aware SQL predicates.
export const STATUS_ALIASES = ["inForce", "expiring", "lost"] as const;
export type StatusAlias = (typeof STATUS_ALIASES)[number];

export type PolicyStatusFilter = PolicyStatusLiteral | StatusAlias;
export type InsuredStatusFilter = StatusAlias;

// ─── Page envelope ───
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Stats response ───
export interface StatsBucket {
  count: number;
  premium: number;
}
export interface StatsTriple {
  inForce: StatsBucket;
  expiring: StatsBucket;
  lost: StatsBucket;
}

// ─── Premium-by-month ───
export interface PremiumMonthPoint {
  month: string;
  total: number;
}

// ─── Augmented insureds-list row ───
// Locked decision #6: PURELY ADDITIVE. Every existing Insured field is
// preserved with identical name + type. Derived fields are appended
// below. Detail endpoints continue to return raw Insured.
//
// Orphan ("Unmatched") rows have id: null and most other Insured fields
// null/undefined; their derived fields still apply. The TS type widens
// `id` to nullable for that case.
export type AugmentedInsuredRow = Omit<Insured, "id"> & {
  id: string | null;
  activePolicyCount: number;
  policyCount: number;
  hasUpcomingRenewal: boolean;
  hasRecentLoss: boolean;
  earliestEffectiveDate: string | null;
  unmatched: boolean;
  // CSR-674 R-002: sum of premiums across in-force-today policies for
  // the insured. Additive widening — same shape rule as the other
  // derived fields. Returned as a finite number; NULL premiums are
  // coerced to 0 in SQL. Drives `sort=premium` (used by the dashboard
  // "Premium Contributors" dialog).
  activePremium: number;
};

// ─── Defaults & clamps ───
export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 100;
export const INSURED_POLICIES_CAP = 500;

export interface ParsedPagination {
  page: number;
  pageSize: number;
  q: string | undefined;
}

export function parsePaginationParams(query: Record<string, unknown>): ParsedPagination {
  const page = clampInt(query.page, 1, 1, Number.MAX_SAFE_INTEGER);
  const pageSize = clampPageSize(query.pageSize);
  const qRaw = typeof query.q === "string" ? query.q.trim() : "";
  const q = qRaw.length > 0 ? qRaw : undefined;
  return { page, pageSize, q };
}

export function parsePolicySortParam(
  raw: unknown,
  dir: unknown,
): { sort: PolicySortField; sortDir: SortDir } {
  const sort = (POLICY_SORT_FIELDS as readonly string[]).includes(String(raw))
    ? (raw as PolicySortField)
    : "createdAt";
  const sortDir: SortDir = dir === "asc" ? "asc" : "desc";
  return { sort, sortDir };
}

export function parseInsuredSortParam(
  raw: unknown,
  dir: unknown,
): { sort: InsuredSortField | null; sortDir: SortDir } {
  // null sentinel = use the locked default
  // (MIN(p.effective_date) DESC NULLS LAST)
  const sort = (INSURED_SORT_FIELDS as readonly string[]).includes(String(raw))
    ? (raw as InsuredSortField)
    : null;
  const sortDir: SortDir = dir === "asc" ? "asc" : "desc";
  return { sort, sortDir };
}

// `valid: false` → route returns 400 per spec.
export function parsePolicyStatusFilter(raw: unknown): {
  value: PolicyStatusFilter | undefined;
  valid: boolean;
} {
  if (raw === undefined || raw === "" || raw === null) return { value: undefined, valid: true };
  const s = String(raw);
  if ((POLICY_STATUS_LITERALS as readonly string[]).includes(s)) {
    return { value: s as PolicyStatusLiteral, valid: true };
  }
  if ((STATUS_ALIASES as readonly string[]).includes(s)) {
    return { value: s as StatusAlias, valid: true };
  }
  return { value: undefined, valid: false };
}

export function parseInsuredStatusFilter(raw: unknown): {
  value: InsuredStatusFilter | undefined;
  valid: boolean;
} {
  if (raw === undefined || raw === "" || raw === null) return { value: undefined, valid: true };
  const s = String(raw);
  if ((STATUS_ALIASES as readonly string[]).includes(s)) {
    return { value: s as InsuredStatusFilter, valid: true };
  }
  return { value: undefined, valid: false };
}

// ─── Intercom Events (CSR shared-table) ───
export const INTERCOM_SORT_FIELDS = [
  "receivedAt",
  "processingStatus",
  "topic",
] as const;
export type IntercomSortField = (typeof INTERCOM_SORT_FIELDS)[number];

export const INTERCOM_STATUS_FILTERS = [
  "received",
  "processing",
  "processed",
  "skipped",
  "failed",
] as const;
export type IntercomStatusFilter = (typeof INTERCOM_STATUS_FILTERS)[number];

export function parseIntercomSortParam(
  raw: unknown,
  dir: unknown,
): { sort: IntercomSortField; sortDir: SortDir } {
  const sort = (INTERCOM_SORT_FIELDS as readonly string[]).includes(String(raw))
    ? (raw as IntercomSortField)
    : "receivedAt";
  const sortDir: SortDir = dir === "asc" ? "asc" : "desc";
  return { sort, sortDir };
}

export function parseIntercomStatusFilter(raw: unknown): {
  value: IntercomStatusFilter | undefined;
  valid: boolean;
} {
  if (raw === undefined || raw === "" || raw === null) return { value: undefined, valid: true };
  const s = String(raw);
  if ((INTERCOM_STATUS_FILTERS as readonly string[]).includes(s)) {
    return { value: s as IntercomStatusFilter, valid: true };
  }
  return { value: undefined, valid: false };
}

// ─── COI Requests (CSR shared-table) ───
export const COI_REQUEST_SORT_FIELDS = [
  "createdAt",
  "customerName",
  "customerEmail",
  "source",
  "status",
] as const;
export type COIRequestSortField = (typeof COI_REQUEST_SORT_FIELDS)[number];

// CSR-867: `"ams"` is a synthetic source value — used only as a filter
// whitelist entry. It is NEVER persisted to `coi_requests.source` (the
// schema enum does not include it). Rows surfaced as `source: "ams"` are
// synthesized at read-time from orphan `coi_issuances` (issuances that
// have no `coi_requests` row referencing them via `coi_issuance_id`),
// so the CSR-705 worker guard-rail `request.source !== "manual"` remains
// intact by construction.
export const COI_REQUEST_SOURCES = [
  "email",
  "manual",
  "intercom_webhook",
  "coi_form",
  "ams",
] as const;
export type COIRequestSource = (typeof COI_REQUEST_SOURCES)[number];

export const COI_SENT_FILTERS = ["sent", "not_sent"] as const;
export type COISentFilter = (typeof COI_SENT_FILTERS)[number];

export const COI_NOT_SENT_REASONS = [
  "insured_has_no_active_policy",
  "insured_has_multiple_active_policies",
  "insured_has_notes",
  "insured_has_policy_in_renewal_period",
  "requester_not_mapped",
  "insured_not_found",
  "feature_flag_off",
  "certificate_holder_not_found",
  "asking_for_modifications",
  "unmapped_request_ff_off",
  "multiple_certificate_holders",
  "has_attachments",
  "incomplete_certificate_holder_data",
  "now_certs_api_error",
  "openai_extraction_failed",
  "not_coi_related",
  "carrier_not_in_rollout",
  // CSR-1321: COI Form auto-send was gated off by a feature flag
  // (`feature_flag_coi_form_auto_send` or the redirect backstop) while the
  // validation itself was clean. Stamped by the intake fall-through in
  // routes.ts so the COI Requests table renders an actionable label.
  "auto_send_disabled",
  "requested_assets_not_on_policy",
  // CSR-1065: terminal exit reason set by the intercom-webhook process body
  // when `unmappedFlow` throws a non-transport error. See `coiRelevance.ts`.
  "unmapped_flow_error",
  // CSR-1133: COI Form Intercom-ticket fall-through failed (throw) or
  // env-unconfigured; see routes.ts COI Form block.
  "intercom_ticket_failed",
  // CSR-625: not-a-request gate auto-handled a thank-you / acknowledgment
  // message before the unmapped flow could wrongly auto-send a COI.
  "not_a_request",
  // CSR-881: synthetic reason for E2E test traffic — keeps the "NOT SENT
  // REASON" filter on the COI Requests page free of false positives that
  // would otherwise land under `insured_not_found`.
  "e2e_test",
  // CSR-1307: auto-send eligibility Gate 5 — no active policy carries a real
  // coverage line, so the COI would render blank. Manual review required.
  "policy_has_no_coverage",
  // CSR-1385: inbound-email insured resolved, but every policy it has is
  // cancelled/expired (no COI-eligible policy). Kept distinct from
  // `insured_has_no_active_policy` so the table reads the cancellation clearly.
  "insured_cancelled",
  // CSR-1441: reasons stamped on `unhandled` rows — COI-related Intercom
  // conversations the pipeline dropped before processing. Deliberately NOT
  // in `NON_REQUEST_NOT_SENT_REASONS` — they must stay in the default list.
  "reply_to_coi",
  "excluded_phrase",
  // CSR-1759: the requester selected the renewal term on the COI Form —
  // auto-send is refused and the request routes to a licensed agent for
  // review. Stamped by the intake gate in routes.ts.
  "renewal_term_requested",
] as const;
export type COINotSentReason = (typeof COI_NOT_SENT_REASONS)[number];

// CSR-1355 — Reasons that mean "this inbound was never a COI request at all".
// Rows stamped with one of these are noise in the default COI Requests list,
// so `getCOIRequests` excludes them unless the caller passes an explicit
// `notSentReasons` filter (deliberately inspecting them). Kept narrow on
// purpose: only reasons that classify the message itself as a non-request
// belong here. NOT included and why:
//   - `e2e_test` is synthetic test traffic, not a misclassified inbound, and
//     already has its own dedicated filter handling; product scope here is the
//     two "not a real request" reasons named in the ticket.
//   - Operational/eligibility skips (`has_attachments`, `insured_not_found`,
//     `auto_send_disabled`, `policy_has_no_coverage`, etc.) ARE genuine COI
//     requests that simply weren't auto-sent — they must stay visible.
export const NON_REQUEST_NOT_SENT_REASONS: readonly COINotSentReason[] = [
  "not_coi_related",
  "not_a_request",
];

// CSR-867 cycle 4 — Single source of truth for display labels of
// `coi_requests.source` and `coi_requests.not_sent_reason`. Used by:
//  - Client (`SystemSettings.tsx`) for table/filter chip labels.
//  - Server (`buildCoiRequestsCsv` in `routes.ts`) for CSV export labels.
// Keeping both ends in sync prevents the CSV-vs-UI divergence that the
// Smoker caught in cycle 3 (CSV exported raw enum values).
export const COI_REQUEST_SOURCE_LABELS: Record<COIRequestSource, string> = {
  email: "Email",
  manual: "Manual",
  intercom_webhook: "Intercom Webhook",
  coi_form: "COI Form",
  // Synthetic source — see comment on `COI_REQUEST_SOURCES`.
  ams: "AMS",
};

export const COI_NOT_SENT_REASON_LABELS: Record<COINotSentReason, string> = {
  insured_has_no_active_policy: "Insured Has No Active Policy",
  insured_has_multiple_active_policies: "Insured Has Multiple Active Policies",
  insured_has_notes: "Insured Has Notes",
  insured_has_policy_in_renewal_period: "Insured Has Policy In Renewal Period",
  requester_not_mapped: "Requester Not Mapped",
  insured_not_found: "Insured Not Found",
  feature_flag_off: "Feature Flag Off",
  certificate_holder_not_found: "Certificate Holder Not Found",
  asking_for_modifications: "Asking For Modifications",
  unmapped_request_ff_off: "Unmapped Request FF Off",
  multiple_certificate_holders: "Multiple Certificate Holders",
  has_attachments: "Document Requirements",
  incomplete_certificate_holder_data: "Incomplete Certificate Holder Data",
  now_certs_api_error: "Eligibility Check Error",
  openai_extraction_failed: "OpenAI Extraction Failed",
  not_coi_related: "Not COI Related",
  carrier_not_in_rollout: "Carrier Not In Rollout",
  auto_send_disabled: "Auto-Send Disabled",
  requested_assets_not_on_policy: "Requested Assets Not On Policy",
  unmapped_flow_error: "Unmapped Flow Error",
  intercom_ticket_failed: "Intercom Ticket Failed",
  not_a_request: "Not A Request",
  e2e_test: "E2E Test",
  policy_has_no_coverage: "Policy Has No Coverage",
  insured_cancelled: "Insured Cancelled",
  reply_to_coi: "Reply To COI",
  excluded_phrase: "Excluded Phrase",
  renewal_term_requested: "Renewal Term Requested",
};

// CSR-935 — Sources whose sent COIs have no user attribution because the
// pipeline runs unattended. The COI Requests "Sent By" column renders
// "Automation" for these instead of an em-dash. Keep this list narrow: only
// add a source here when its sent rows are guaranteed to have
// `issuedByName = null` (writer-side) AND product wants the attribution.
export const AUTOMATED_COI_REQUEST_SOURCES: readonly COIRequestSource[] = [
  "intercom_webhook",
  "coi_form",
];

// CSR-935 — Single source of truth for the "Sent By" attribution string
// shared by the UI table cell (`SystemSettings.tsx`) and CSV export
// (`buildCoiRequestsCsv` in `routes.ts`). Returns `null` when there is no
// attribution to render so callers can supply their own placeholder
// (`"—"` in the UI, empty string in CSV — matches CSR-867 parity tests).
//
// Rules:
//   - !coiWasSent → null  (no attribution for unsent requests)
//   - issuedByName present → that name (manual AMS sends — unchanged)
//   - source in AUTOMATED_COI_REQUEST_SOURCES → "Automation"
//   - otherwise → null
export function formatCoiRequestSentBy(row: {
  issuedByName: string | null;
  source: string;
  coiWasSent: boolean;
}): string | null {
  if (!row.coiWasSent) return null;
  if (row.issuedByName) return row.issuedByName;
  if ((AUTOMATED_COI_REQUEST_SOURCES as readonly string[]).includes(row.source)) {
    return "Automation";
  }
  return null;
}

export function parseCOIRequestSortParam(
  raw: unknown,
  dir: unknown,
): { sort: COIRequestSortField; sortDir: SortDir } {
  const sort = (COI_REQUEST_SORT_FIELDS as readonly string[]).includes(String(raw))
    ? (raw as COIRequestSortField)
    : "createdAt";
  const sortDir: SortDir = dir === "asc" ? "asc" : "desc";
  return { sort, sortDir };
}

export function parseCOISourceFilter(raw: unknown): {
  value: COIRequestSource | undefined;
  valid: boolean;
} {
  if (raw === undefined || raw === "" || raw === null) return { value: undefined, valid: true };
  const s = String(raw);
  if ((COI_REQUEST_SOURCES as readonly string[]).includes(s)) {
    return { value: s as COIRequestSource, valid: true };
  }
  return { value: undefined, valid: false };
}

export function parseCOISentFilter(raw: unknown): {
  value: COISentFilter | undefined;
  valid: boolean;
} {
  if (raw === undefined || raw === "" || raw === null) return { value: undefined, valid: true };
  const s = String(raw);
  if ((COI_SENT_FILTERS as readonly string[]).includes(s)) {
    return { value: s as COISentFilter, valid: true };
  }
  return { value: undefined, valid: false };
}

export function parseCOINotSentReasons(raw: unknown): {
  value: COINotSentReason[];
  valid: boolean;
} {
  if (raw === undefined || raw === null || raw === "") {
    return { value: [], valid: true };
  }
  const whitelist = COI_NOT_SENT_REASONS as readonly string[];
  const inputs = Array.isArray(raw) ? raw : [raw];
  const out: COINotSentReason[] = [];
  for (const item of inputs) {
    if (typeof item !== "string" || item === "") continue;
    if (!whitelist.includes(item)) {
      return { value: [], valid: false };
    }
    out.push(item as COINotSentReason);
  }
  return { value: out, valid: true };
}

export function parseIncludeUnmatched(raw: unknown): boolean {
  if (raw === undefined || raw === null || raw === "") return true;
  return String(raw).toLowerCase() !== "false";
}

// CSR-867 cycle 5 — parse `createdAtFrom` / `createdAtTo` query params on
// the COI Requests list and CSV export routes. The PM asked for a user-
// facing date range filter on `Created At` (which maps to
// `coi_issuances.issued_at` on the orphan side of the UNION). Both
// parameters are independently optional; either may be present without
// the other. Format is strict ISO `yyyy-mm-dd` — anything else is a
// client error (route returns 400).
//
// Returns `{ from, to, valid }` where `from`/`to` are `Date` instances at
// the start of the local day (midnight). The storage layer is
// responsible for the inclusive end-of-day semantics (see
// `getCOIRequests` — the upper bound is rendered as `< to + 1 day`).
//
// Cross-bound validation (`from > to`) is intentionally NOT performed
// here: the client clamps for usability (don't block the form), and the
// SQL predicate naturally returns zero rows for an inverted range.
export interface ParsedCoiRequestDateFilter {
  from: Date | undefined;
  to: Date | undefined;
  valid: boolean;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseIsoDateOnly(raw: unknown): { value: Date | undefined; valid: boolean } {
  if (raw === undefined || raw === null || raw === "") {
    return { value: undefined, valid: true };
  }
  if (typeof raw !== "string") return { value: undefined, valid: false };
  if (!ISO_DATE_RE.test(raw)) return { value: undefined, valid: false };
  // Construct with explicit Y/M/D so we always anchor at local midnight.
  // `new Date("2026-05-14")` is UTC midnight which shifts the date in
  // negative-offset zones (e.g. US Pacific shows `2026-05-13`). Anchoring
  // to local midnight matches what the user typed in their local zone.
  const [y, m, d] = raw.split("-").map((s) => Number.parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return { value: undefined, valid: false };
  }
  // Month/day range check — `new Date(2026, 1, 31)` silently rolls over
  // to March, but the user expects strict validation.
  if (m < 1 || m > 12) return { value: undefined, valid: false };
  if (d < 1 || d > 31) return { value: undefined, valid: false };
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    // Rollover happened (e.g. Feb 30 → Mar 2) — reject.
    return { value: undefined, valid: false };
  }
  return { value: date, valid: true };
}

export function parseCoiRequestDateFilter(
  fromRaw: unknown,
  toRaw: unknown,
): ParsedCoiRequestDateFilter {
  const fromParse = parseIsoDateOnly(fromRaw);
  if (!fromParse.valid) return { from: undefined, to: undefined, valid: false };
  const toParse = parseIsoDateOnly(toRaw);
  if (!toParse.valid) return { from: undefined, to: undefined, valid: false };
  return { from: fromParse.value, to: toParse.value, valid: true };
}

function clampInt(raw: unknown, fallback: number, min: number, max: number): number {
  const n = typeof raw === "string" ? parseInt(raw, 10) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return fallback;
  if (n > max) return max;
  return Math.floor(n);
}

function clampPageSize(raw: unknown): number {
  const n = typeof raw === "string" ? parseInt(raw, 10) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return PAGE_SIZE_DEFAULT;
  if (n < 1) return PAGE_SIZE_DEFAULT;
  if (n > PAGE_SIZE_MAX) return PAGE_SIZE_MAX;
  return Math.floor(n);
}

// Today as YYYY-MM-DD in server local time. Matches Policies.tsx's
// existing `today.toISOString().split("T")[0]` formulation.
export function todayDateString(now: Date = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .toISOString()
    .split("T")[0]!;
}

/**
 * CSR-674 R-005: escape Postgres LIKE/ILIKE wildcards (`%`, `_`, `\`)
 * inside user-controlled search input. Without this, a literal user
 * search for `"50%"` becomes `"%50%%"` and matches anything that
 * contains `"50"` followed by zero-or-more characters — same effective
 * result by accident, but `"50_"` would match any 4-char string
 * containing `"50"`. Bind value is still SQL-injection-safe via
 * parameter binding; this just keeps the substring search predictable.
 */
export function escapeIlikePattern(q: string): string {
  // Backslash first — otherwise it would re-escape the escapes we add.
  return q.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export { IN_FORCE_STATUSES };
